import type { IconName } from '../components';
import type { MeContextValue } from './me';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: IconName;
  /** Exact match for the active state (home). */
  end?: boolean;
  /** Shown in the phone bottom tab bar. */
  phoneTab?: boolean;
  show: (m: MeContextValue) => boolean;
}

/** Role-aware navigation, driven by /me permissions (ADR-0005). */
export const NAV: NavItem[] = [
  { to: '/', labelKey: 'ui.nav.home', icon: 'home', end: true, phoneTab: true, show: () => true },
  { to: '/sites', labelKey: 'ui.nav.sites', icon: 'mapPin', phoneTab: true, show: (m) => m.canAny('survey.create', 'masterdata.view') },
  { to: '/surveys', labelKey: 'ui.nav.surveys', icon: 'clipboard', phoneTab: true, show: (m) => m.canAny('survey.create', 'survey.view') },
  { to: '/report', labelKey: 'ui.nav.report', icon: 'alert', show: (m) => m.can('event.report') },
  { to: '/events', labelKey: 'ui.nav.events', icon: 'alertCircle', show: (m) => m.canAny('event.report', 'event.view') },
  { to: '/review', labelKey: 'ui.nav.review', icon: 'listChecks', show: (m) => m.can('survey.review') },
  { to: '/findings', labelKey: 'ui.nav.findings', icon: 'flag', show: (m) => m.can('finding.view') },
  { to: '/overdue', labelKey: 'ui.nav.overdue', icon: 'clock', show: (m) => m.canAny('survey.schedule', 'reports.view') },
  { to: '/insights', labelKey: 'ui.nav.insights', icon: 'globe', show: (m) => m.can('insights.view') },
  { to: '/summaries', labelKey: 'ui.nav.summaries', icon: 'fileText', show: (m) => m.canAny('event.triage', 'survey.communicate') },
  { to: '/dashboard', labelKey: 'ui.nav.dashboard', icon: 'dashboard', show: (m) => m.can('reports.view') },
  { to: '/safety', labelKey: 'ui.nav.safety', icon: 'shield', show: (m) => m.can('safety.command') },
  // Shown whenever at least one admin section is (same rules, one list: ADMIN_SECTIONS).
  { to: '/admin', labelKey: 'ui.nav.admin', icon: 'settings', show: (m) => ADMIN_SECTIONS.some((s) => s.show(m)) },
  { to: '/profile', labelKey: 'ui.nav.profile', icon: 'user', phoneTab: true, show: () => true },
];

export interface AdminSection {
  to: string;
  /** Area key for the admin screens' titles and descriptions (ui.adminx.areas.<key>). */
  key: string;
  labelKey: string;
  icon: IconName;
  show: (m: MeContextValue) => boolean;
}

/**
 * Admin areas and who sees them — the single list behind the Admin nav entry,
 * the admin home and the admin sub-navigation. Templates are shown to people who
 * design or publish them: every default role holds template.view only so the
 * app can load survey forms, which is no reason to show them an Admin menu.
 * Roles and Legal texts are shown to scoped holders read-only; editing them
 * needs the permission globally (see RolesPage, LegalTextsPage).
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  { to: '/admin/users', key: 'users', labelKey: 'ui.admin.users', icon: 'users', show: (m) => m.can('users.view') },
  { to: '/admin/customers', key: 'customers', labelKey: 'ui.adminx.areas.customers.title', icon: 'building', show: (m) => m.canAny('masterdata.edit', 'survey.schedule') },
  { to: '/admin/sites', key: 'sites', labelKey: 'ui.adminx.areas.sites.title', icon: 'mapPin', show: (m) => m.canAny('masterdata.edit', 'survey.schedule') },
  { to: '/admin/roles', key: 'roles', labelKey: 'ui.admin.roles', icon: 'key', show: (m) => m.can('roles.manage') },
  { to: '/admin/org', key: 'org', labelKey: 'ui.adminx.areas.org.title', icon: 'building', show: (m) => m.can('settings.manage') },
  { to: '/admin/settings', key: 'settings', labelKey: 'ui.admin.settings', icon: 'sliders', show: (m) => m.can('settings.manage') },
  { to: '/admin/legal-texts', key: 'legal', labelKey: 'ui.admin.legalTexts', icon: 'fileText', show: (m) => m.can('settings.manage') },
  // System Admins only (settings.manage at the global root), like the API.
  {
    to: '/admin/legal-holds',
    key: 'legalHolds',
    labelKey: 'ui.adminx.areas.legalHolds.title',
    icon: 'shield',
    show: (m) => (m.me.permissions['settings.manage'] ?? []).some((s) => s.path === 'global' && !s.customerId && !s.siteId && !s.serviceLine),
  },
  { to: '/admin/catalogue', key: 'catalogue', labelKey: 'ui.adminx.areas.catalogue.title', icon: 'layers', show: (m) => m.can('catalogue.manage') },
  { to: '/admin/intel', key: 'intel', labelKey: 'ui.adminx.areas.intel.title', icon: 'globe', show: (m) => m.can('intel.manage') },
  { to: '/admin/imports', key: 'imports', labelKey: 'ui.admin.imports', icon: 'upload', show: (m) => m.can('masterdata.import') },
  { to: '/admin/templates', key: 'templates', labelKey: 'ui.admin.templates', icon: 'layers', show: (m) => m.canAny('template.design', 'template.publish') },
  { to: '/admin/api-clients', key: 'apiClients', labelKey: 'ui.admin.apiClients', icon: 'globe', show: (m) => m.can('api.keys.manage') },
  { to: '/admin/audit', key: 'audit', labelKey: 'ui.admin.audit', icon: 'eye', show: (m) => m.can('audit.view') },
];
